import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, SEISMIC_CATEGORIES } from "./structural-data";

const ResultCard = ({ label, value, unit, accent }: { label: string; value: string; unit: string; accent?: boolean }) => (
  <div className="trade-card flex justify-between items-center">
    <span className="text-sm text-muted-foreground">{label}</span>
    <span className={`font-mono-data text-lg font-bold ${accent ? "text-primary" : "text-foreground"}`}>
      {value} {unit && <span className="text-xs text-muted-foreground font-normal">{unit}</span>}
    </span>
  </div>
);

/* ═══════════════════════════════════════════════════════
   SEISMIC FORCE CALCULATOR (ASCE 7 / IBC)
   Fp = (0.4 × ap × SDS × Wp / Rp) × (1 + 2 × z/h)
   ═══════════════════════════════════════════════════════ */

export const SeismicForceCalc = () => {
  const [sds, setSds] = useState("");
  const [wp, setWp] = useState("");
  const [ap, setAp] = useState("2.5");
  const [rp, setRp] = useState("6");
  const [ip, setIp] = useState("1");
  const [zRatio, setZRatio] = useState("0.5"); // z/h

  const sdsVal = parseFloat(sds);
  const wpVal = parseFloat(wp);
  const apVal = parseFloat(ap);
  const rpVal = parseFloat(rp);
  const ipVal = parseFloat(ip);
  const zH = parseFloat(zRatio);

  const hasInput = sdsVal > 0 && wpVal > 0 && rpVal > 0;

  // Fp = (0.4 × ap × SDS × Ip × Wp / Rp) × (1 + 2 × z/h)
  const fp = hasInput ? (0.4 * apVal * sdsVal * ipVal * wpVal / rpVal) * (1 + 2 * zH) : 0;
  // Max: Fp = 1.6 × SDS × Ip × Wp
  const fpMax = hasInput ? 1.6 * sdsVal * ipVal * wpVal : 0;
  // Min: Fp = 0.3 × SDS × Ip × Wp
  const fpMin = hasInput ? 0.3 * sdsVal * ipVal * wpVal : 0;
  const fpDesign = hasInput ? Math.min(Math.max(fp, fpMin), fpMax) : 0;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">seismic design force</span> (Fp) for non-structural components per ASCE 7-22 / IBC. Fp = (0.4 × ap × SDS × Ip × Wp / Rp) × (1 + 2z/h)
        </p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">SDS (Site Accel.)</label>
            <Input type="number" inputMode="decimal" placeholder="g…" value={sds} onChange={(e) => setSds(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Wp (Weight, lb)</label>
            <Input type="number" inputMode="decimal" placeholder="lbs…" value={wp} onChange={(e) => setWp(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">ap</label>
            <Select value={ap} onValueChange={setAp}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1.0">1.0 (Rigid)</SelectItem>
                <SelectItem value="2.5">2.5 (Flexible)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Rp</label>
            <Select value={rp} onValueChange={setRp}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1.5">1.5 (Low ductility)</SelectItem>
                <SelectItem value="2.5">2.5 (Limited)</SelectItem>
                <SelectItem value="3">3.0 (Moderate)</SelectItem>
                <SelectItem value="4.5">4.5 (High)</SelectItem>
                <SelectItem value="6">6.0 (Vibration isolated)</SelectItem>
                <SelectItem value="9">9.0 (Piping, some)</SelectItem>
                <SelectItem value="12">12.0 (Duct/piping w/ bracing)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Ip</label>
            <Select value={ip} onValueChange={setIp}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1.0 (Ordinary)</SelectItem>
                <SelectItem value="1.5">1.5 (Essential)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">z/h (Height Ratio: 0=ground, 1=roof)</label>
          <Select value={zRatio} onValueChange={setZRatio}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 (Ground level)</SelectItem>
              <SelectItem value="0.25">0.25</SelectItem>
              <SelectItem value="0.5">0.5 (Mid-height)</SelectItem>
              <SelectItem value="0.75">0.75</SelectItem>
              <SelectItem value="1">1.0 (Roof level)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Design Fp" value={fmt(fpDesign, 0)} unit="lb" accent />
          <ResultCard label="Calculated Fp" value={fmt(fp, 0)} unit="lb" />
          <ResultCard label="Fp Max" value={fmt(fpMax, 0)} unit="lb" />
          <ResultCard label="Fp Min" value={fmt(fpMin, 0)} unit="lb" />
          {fp > fpMax && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Calculated Fp exceeds max — design force capped at Fp,max.</p>
            </div>
          )}
          {fp < fpMin && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3">
              <p className="text-xs text-primary font-semibold">ℹ️ Calculated Fp below minimum — design force raised to Fp,min.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   ANCHOR BOLT SIZING CALCULATOR
   ═══════════════════════════════════════════════════════ */

const ANCHOR_DATA = [
  { dia: "1/4\"", tension: 530, shear: 640 },
  { dia: "3/8\"", tension: 1200, shear: 1480 },
  { dia: "1/2\"", tension: 2150, shear: 2680 },
  { dia: "5/8\"", tension: 3480, shear: 4200 },
  { dia: "3/4\"", tension: 5090, shear: 6130 },
  { dia: "7/8\"", tension: 6930, shear: 6880 },
  { dia: "1\"", tension: 8700, shear: 8720 },
];

export const AnchorBoltCalc = () => {
  const [tensionLoad, setTensionLoad] = useState("");
  const [shearLoad, setShearLoad] = useState("");
  const [numBolts, setNumBolts] = useState("4");
  const [safetyFactor, setSafetyFactor] = useState("4");

  const tension = parseFloat(tensionLoad) || 0;
  const shear = parseFloat(shearLoad) || 0;
  const bolts = parseInt(numBolts) || 4;
  const sf = parseFloat(safetyFactor) || 4;

  const tensionPerBolt = tension / bolts;
  const shearPerBolt = shear / bolts;
  const requiredTension = tensionPerBolt * sf;
  const requiredShear = shearPerBolt * sf;

  const hasInput = (tension > 0 || shear > 0) && bolts > 0;

  const result = useMemo(() => {
    if (!hasInput) return null;
    for (const a of ANCHOR_DATA) {
      if (a.tension >= requiredTension && a.shear >= requiredShear) {
        const tensionUtil = (requiredTension / a.tension) * 100;
        const shearUtil = (requiredShear / a.shear) * 100;
        // Interaction: (tension/capacity)^(5/3) + (shear/capacity)^(5/3) ≤ 1.0
        const interaction = Math.pow(tensionPerBolt * sf / a.tension, 5/3) + Math.pow(shearPerBolt * sf / a.shear, 5/3);
        return { ...a, tensionUtil, shearUtil, interaction };
      }
    }
    return null;
  }, [hasInput, requiredTension, requiredShear, tensionPerBolt, shearPerBolt, sf]);

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Size <span className="text-foreground font-semibold">expansion anchor bolts</span> in 3000 PSI concrete based on applied loads, number of anchors, and safety factor.
        </p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Total Tension (lb)</label>
            <Input type="number" inputMode="decimal" placeholder="lbs…" value={tensionLoad} onChange={(e) => setTensionLoad(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Total Shear (lb)</label>
            <Input type="number" inputMode="decimal" placeholder="lbs…" value={shearLoad} onChange={(e) => setShearLoad(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Number of Bolts</label>
            <Select value={numBolts} onValueChange={setNumBolts}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1,2,3,4,6,8].map(n => <SelectItem key={n} value={String(n)}>{n} bolts</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Safety Factor</label>
            <Select value={safetyFactor} onValueChange={setSafetyFactor}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="2">2× (light duty)</SelectItem>
                <SelectItem value="3">3× (general)</SelectItem>
                <SelectItem value="4">4× (overhead/seismic)</SelectItem>
                <SelectItem value="5">5× (life safety)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Min. Bolt Diameter" value={result.dia} unit="" accent />
          <ResultCard label="Tension per Bolt" value={fmt(tensionPerBolt, 0)} unit="lb" />
          <ResultCard label="Shear per Bolt" value={fmt(shearPerBolt, 0)} unit="lb" />
          <ResultCard label="Tension Utilization" value={fmt(result.tensionUtil, 1)} unit="%" />
          <ResultCard label="Shear Utilization" value={fmt(result.shearUtil, 1)} unit="%" />
          <ResultCard label="Interaction Ratio" value={fmt(result.interaction, 3)} unit="≤1.0" />
          {result.interaction > 0.9 && result.interaction <= 1.0 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Near capacity — consider upsizing or adding bolts.</p>
            </div>
          )}
        </div>
      )}
      {hasInput && !result && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3 animate-fade-in">
          <p className="text-xs text-destructive font-semibold">⚠️ Load exceeds capacity of standard expansion anchors. Consider undercut or adhesive anchors, or increase bolt count.</p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SIMPLE BEAM LOAD CALCULATOR
   (Uniformly loaded, simple span, select material)
   ═══════════════════════════════════════════════════════ */

export const BeamLoadCalc = () => {
  const [span, setSpan] = useState("");
  const [load, setLoad] = useState(""); // PLF
  const [fb, setFb] = useState("1000"); // allowable bending stress PSI

  const L = parseFloat(span);
  const w = parseFloat(load);
  const Fb = parseFloat(fb);
  const hasInput = L > 0 && w > 0 && Fb > 0;

  // M = wL²/8 (in-lb), S_req = M/Fb
  const totalLoad = w * L;
  const maxMoment = (w * L * L) / 8; // ft-lb
  const maxMomentInLb = maxMoment * 12; // in-lb
  const sRequired = maxMomentInLb / Fb; // in³
  const maxShear = (w * L) / 2; // lb
  const deflection5L384 = (5 * w * Math.pow(L, 4) * 1728) / 384; // numerator for deflection = 5wL⁴/(384EI)

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">bending moment</span>, required section modulus, and reactions for a simply supported, uniformly loaded beam.
        </p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Span (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="ft…" value={span} onChange={(e) => setSpan(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Load (lb/ft)</label>
            <Input type="number" inputMode="decimal" placeholder="PLF…" value={load} onChange={(e) => setLoad(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Allowable Fb (PSI)</label>
          <Select value={fb} onValueChange={setFb}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="875">875 (SPF #2)</SelectItem>
              <SelectItem value="1000">1,000 (Doug Fir #2)</SelectItem>
              <SelectItem value="1350">1,350 (So. Pine #2)</SelectItem>
              <SelectItem value="2400">2,400 (LVL)</SelectItem>
              <SelectItem value="24000">24,000 (A36 Steel)</SelectItem>
              <SelectItem value="33000">33,000 (A992 Steel)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Max Moment" value={fmt(maxMoment, 0)} unit="ft-lb" accent />
          <ResultCard label="Required Sx" value={fmt(sRequired, 2)} unit="in³" accent />
          <ResultCard label="Total Load" value={fmt(totalLoad, 0)} unit="lb" />
          <ResultCard label="Max Shear (reaction)" value={fmt(maxShear, 0)} unit="lb" />
          <div className="bg-card border border-border rounded-xl p-4 mt-2">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Formulas</h3>
            <div className="space-y-1.5 text-xs font-mono-data text-muted-foreground">
              <p>M = wL²/8 = {w} × {L}² / 8 = <span className="text-primary font-bold">{fmt(maxMoment, 0)} ft-lb</span></p>
              <p>S = M/Fb = {fmt(maxMomentInLb, 0)} / {Fb} = <span className="text-primary font-bold">{fmt(sRequired, 2)} in³</span></p>
              <p>V = wL/2 = {w} × {L} / 2 = <span className="text-primary font-bold">{fmt(maxShear, 0)} lb</span></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   POINT LOAD DEFLECTION CALCULATOR
   ═══════════════════════════════════════════════════════ */

export const DeflectionCalc = () => {
  const [span, setSpan] = useState("");
  const [load, setLoad] = useState(""); // PLF
  const [eVal, setEVal] = useState("1700000"); // E (PSI)
  const [iVal, setIVal] = useState("");

  const L = parseFloat(span);
  const w = parseFloat(load);
  const E = parseFloat(eVal);
  const I = parseFloat(iVal);
  const hasInput = L > 0 && w > 0 && E > 0 && I > 0;

  // δ = 5wL⁴ / (384EI), w in lb/in, L in inches
  const wPerIn = w / 12;
  const Linches = L * 12;
  const deflection = hasInput ? (5 * wPerIn * Math.pow(Linches, 4)) / (384 * E * I) : 0;
  const L360 = L * 12 / 360;
  const L240 = L * 12 / 240;
  const L180 = L * 12 / 180;

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">beam deflection</span> for uniform load on a simple span. δ = 5wL⁴ / 384EI
        </p>
      </div>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Span (ft)</label>
            <Input type="number" inputMode="decimal" placeholder="ft…" value={span} onChange={(e) => setSpan(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">Load (lb/ft)</label>
            <Input type="number" inputMode="decimal" placeholder="PLF…" value={load} onChange={(e) => setLoad(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">E (PSI)</label>
            <Select value={eVal} onValueChange={setEVal}>
              <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1400000">1.4M (SPF #2)</SelectItem>
                <SelectItem value="1700000">1.7M (Doug Fir #2)</SelectItem>
                <SelectItem value="1800000">1.8M (So. Pine #2)</SelectItem>
                <SelectItem value="1900000">1.9M (LVL)</SelectItem>
                <SelectItem value="29000000">29M (Steel)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">I (in⁴)</label>
            <Input type="number" inputMode="decimal" placeholder="in⁴…" value={iVal} onChange={(e) => setIVal(e.target.value)} className="font-mono-data bg-card border-border" />
          </div>
        </div>
      </div>
      {hasInput && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Deflection" value={fmt(deflection, 4)} unit="in" accent />
          <ResultCard label="L/360 Limit" value={fmt(L360, 3)} unit="in" />
          <ResultCard label="L/240 Limit" value={fmt(L240, 3)} unit="in" />
          {deflection > L360 && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-3">
              <p className="text-xs text-destructive font-semibold">⚠️ Exceeds L/360 — not acceptable for floor live load.</p>
            </div>
          )}
          {deflection <= L360 && deflection > L240 && (
            <div className="bg-safety/10 border border-safety/30 rounded-xl p-3">
              <p className="text-xs text-safety font-semibold">⚡ Passes L/360 but exceeds L/240. Check total load criteria.</p>
            </div>
          )}
          {deflection <= L360 && (
            <div className="bg-primary/10 border border-primary/30 rounded-xl p-3">
              <p className="text-xs text-primary font-semibold">✓ Within L/360 deflection limit.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   SDC LOOKUP
   ═══════════════════════════════════════════════════════ */

export const SdcLookup = () => (
  <div className="space-y-3">
    <div className="bg-card border border-border rounded-xl p-3">
      <p className="text-xs text-muted-foreground leading-relaxed">
        <span className="text-foreground font-semibold">Seismic Design Categories</span> per IBC / ASCE 7. SDC determines bracing and anchorage requirements.
      </p>
    </div>
    {SEISMIC_CATEGORIES.map(cat => (
      <div key={cat.sdc} className="trade-card flex items-center justify-between">
        <div>
          <span className="font-mono-data text-lg font-bold text-primary">SDC {cat.sdc}</span>
          <p className="text-xs text-muted-foreground">{cat.description}</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">{cat.sds_range} · Ip = {cat.ip}</p>
        </div>
      </div>
    ))}
  </div>
);
